import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";

const TourContext = createContext(null);

// Define all tour steps - will be filtered by user role
const ALL_TOUR_STEPS = [
  // Common steps for all users
  {
    id: "welcome",
    target: null, // Full screen welcome
    title: "Welcome to CONNECT!",
    description: "Let's take a quick tour of your Cloud PBX Operator Console. This will help you get familiar with the key features.",
    position: "center",
    roles: ["all"]
  },
  {
    id: "dialer",
    target: '[data-testid="nav-dialer"]',
    title: "My Phone",
    description: "This is your main phone interface. Make and receive calls, use the dial pad, and access call controls like mute and hold.",
    position: "right",
    roles: ["all"]
  },
  {
    id: "contacts",
    target: '[data-testid="nav-contacts"]',
    title: "Contacts",
    description: "View and manage your contacts. Internal contacts from your PBX and external contacts you add are all here.",
    position: "right",
    roles: ["all"]
  },
  {
    id: "my-calls",
    target: '[data-testid="nav-my-calls"]',
    title: "My Calls",
    description: "See your personal call history - incoming, outgoing, and missed calls with timestamps and durations.",
    position: "right",
    roles: ["all"]
  },
  {
    id: "theme-toggle",
    target: '[data-testid="user-menu-trigger"]',
    title: "Theme & Settings",
    description: "Click your profile to switch between Light and Dark mode, access your settings, restart this tour, or sign out.",
    position: "top",
    roles: ["all"]
  },
  
  // Phone Admin specific steps
  {
    id: "queue-monitor",
    target: '[data-testid="nav-queue-monitor"]',
    title: "Queue Monitor",
    description: "Monitor active calls in real-time. See who's on calls, call durations, and manage your team's workload.",
    position: "right",
    roles: ["phone_admin", "developer"]
  },
  {
    id: "call-history",
    target: '[data-testid="nav-call-history"]',
    title: "Call History",
    description: "As a Phone Admin, you can view all call history across your team - great for monitoring and reporting.",
    position: "right",
    roles: ["phone_admin", "developer"]
  },
  {
    id: "analytics",
    target: '[data-testid="nav-analytics"]',
    title: "Analytics",
    description: "View call statistics and analytics. Track call volumes, durations, and team performance over time.",
    position: "right",
    roles: ["phone_admin", "developer"]
  },
  
  // Customer Admin specific steps
  {
    id: "company-settings",
    target: '[data-testid="nav-company"]',
    title: "Company Settings",
    description: "As a Customer Admin, manage your company settings, PBX integration, and user management here.",
    position: "right",
    roles: ["customer_admin", "developer"]
  },
  
  // Final step
  {
    id: "complete",
    target: null,
    title: "You're All Set!",
    description: "That's the tour! You can restart this walkthrough anytime from your profile menu. Need help? Contact your administrator.",
    position: "center",
    roles: ["all"]
  }
];

export function TourProvider({ children }) {
  const { user, isPhoneAdmin, isCustomerAdmin, isDeveloper } = useAuth();
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [hasCompletedTour, setHasCompletedTour] = useState(false);

  // Get user's roles for filtering steps
  const getUserRoles = useCallback(() => {
    const roles = ["all"];
    if (isPhoneAdmin?.()) roles.push("phone_admin");
    if (isCustomerAdmin?.()) roles.push("customer_admin");
    if (isDeveloper?.()) roles.push("developer");
    return roles;
  }, [isPhoneAdmin, isCustomerAdmin, isDeveloper]);

  // Filter steps based on user's roles
  const getFilteredSteps = useCallback(() => {
    const userRoles = getUserRoles();
    return ALL_TOUR_STEPS.filter(step => {
      // Check if user has any of the required roles
      return step.roles.some(role => userRoles.includes(role));
    }).filter(step => {
      // Skip optional steps that require specific pages if not on that page
      if (step.optional && step.requiresPage) {
        return window.location.pathname === step.requiresPage;
      }
      return true;
    });
  }, [getUserRoles]);

  const steps = getFilteredSteps();
  const currentStep = steps[currentStepIndex];

  // Check if tour has been completed before
  useEffect(() => {
    if (user?.phone_number) {
      const tourKey = `tour_completed_${user.phone_number}`;
      const completed = localStorage.getItem(tourKey) === "true";
      setHasCompletedTour(completed);
      
      // Auto-start tour for new users
      if (!completed && !isActive) {
        // Small delay to let the UI render
        const timer = setTimeout(() => {
          setIsActive(true);
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [user?.phone_number]);

  const startTour = useCallback(() => {
    setCurrentStepIndex(0);
    setIsActive(true);
  }, []);

  const endTour = useCallback((markComplete = true) => {
    setIsActive(false);
    setCurrentStepIndex(0);
    
    if (markComplete && user?.phone_number) {
      const tourKey = `tour_completed_${user.phone_number}`;
      localStorage.setItem(tourKey, "true");
      setHasCompletedTour(true);
    }
  }, [user?.phone_number]);

  const nextStep = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      endTour(true);
    }
  }, [currentStepIndex, steps.length, endTour]);

  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [currentStepIndex]);

  const skipTour = useCallback(() => {
    endTour(true);
  }, [endTour]);

  const value = {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps: steps.length,
    hasCompletedTour,
    startTour,
    endTour,
    nextStep,
    prevStep,
    skipTour
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error("useTour must be used within a TourProvider");
  }
  return context;
}
